<script lang="ts">
	import type { FinancialNumber } from 'financial-number'

	import fnum from 'shared/number'
	import autofocus_directive from 'shared/autofocus_directive'
	import { step_to_precision } from 'shared/precision_and_step'

	export let min: number = 0
	export let max: number = Number.MAX_SAFE_INTEGER
	export let step: number = 1
	export let precision = step_to_precision(step)
	export let value: number | null | FinancialNumber | undefined
	export let placeholder: string | null = null
	export let disabled: boolean | null = null
	export let autofocus: boolean = false
	export let allow_null_or_undefined: boolean = false

	const original_value_was_a_fnum = typeof value === `object` && value !== null

	const to_float = (value: number | null | FinancialNumber | undefined) => allow_null_or_undefined && (value === null || value === undefined)
		? value
		: Number(value)

	let input_value = to_float(value)

	const sync_input_value_with_new_value = (new_value: typeof value) => {
		input_value = to_float(new_value)
	}

	const handle_input_value = (input_value: number | null | undefined) => {
		const is_number = typeof input_value === `number`
		const does_not_exceed_max = max === null || (is_number && input_value <= max)
		const is_valid_number = is_number
			&& input_value >= min
			&& does_not_exceed_max
			&& fnum(input_value.toString()).changePrecision(precision).equal(fnum(input_value.toString()))

		if (is_valid_number || ((input_value === null || input_value === undefined) && allow_null_or_undefined)) {
			if (original_value_was_a_fnum && input_value !== null && input_value !== undefined) {
				value = fnum(input_value.toString()).changePrecision(precision)
			} else {
				value = input_value
			}
		}
	}

	$: typeof min !== `number` && console.error(`min should be of type number instead of "${ typeof min }"`)
	$: max && typeof max !== `number` && console.error(`max should be of type number instead of "${ typeof max }"`)
	$: typeof step !== `number` && console.error(`step should be of type number instead of "${ typeof step }"`)
	$: typeof precision !== `number` && console.error(`precision should be of type number instead of "${ typeof precision }"`)

	$: handle_input_value(input_value)

	$: sync_input_value_with_new_value(value)

	const reset_input_value = () => {
		if (input_value !== to_float(value)) {
			input_value = to_float(value)
		}
	}
</script>

<input
	type="number"
	min={min}
	max={max}
	step={step}
	bind:value={input_value}
	{placeholder}
	{disabled}
	use:autofocus_directive={autofocus}
	on:blur={reset_input_value}
	on:blur
	on:focus
	on:change
	on:input
	{...$$restProps}
/>

<style>
	input {
		text-align: right;
		font-variant-numeric: tabular-nums;
		padding: 0;
		width: 100px;
	}
</style>
